import { Check, Minus, X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { t } from '@/shared/lib/i18n'
import { getCellSymbol } from '@/shared/lib/calendar-cell-symbol'
import type { CellState } from '@/shared/lib/calendar-cell-symbol'

/**
 * Renders the same icon used in CalendarGrid for a given cell state.
 * Delegates to getCellSymbol so icons stay in sync with the grid — single source of truth.
 */
const SymbolIcon = ({ state, dateModified }: { state: CellState; dateModified?: boolean }) => {
  const symbolDef = getCellSymbol(state, { dateModified })
  switch (symbolDef.kind) {
    case 'check':
      return <Check className="h-3.5 w-3.5 text-gray-700" aria-hidden />
    case 'check_date_changed':
      return (
        <span className="relative mr-1.5 inline-flex text-gray-700" aria-hidden>
          <Check className="h-3.5 w-3.5" />
          <span
            className="absolute -right-1.5 -top-1 text-[10px] font-bold leading-none"
            aria-hidden
          >
            *
          </span>
        </span>
      )
    case 'x':
      return <X className="h-3.5 w-3.5 text-gray-700" aria-hidden />
    case 'x_badge':
      return (
        <span className="relative inline-flex" aria-hidden>
          <X className="h-3.5 w-3.5 text-gray-700" />
          <span className="absolute -right-1.5 -top-1.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-gray-700 text-[7px] font-bold text-white leading-none">
            !
          </span>
        </span>
      )
    case 'dash':
      return <Minus className="h-3.5 w-3.5 text-gray-500" aria-hidden />
    case 'letter_o':
      return (
        <span className="text-xs font-bold text-gray-700 leading-none" aria-hidden>
          O
        </span>
      )
    default:
      return null
  }
}

interface LegendEntry {
  id: string
  state: CellState
  dateModified?: boolean
  label: string
  tooltipText: string
}

const buildLegendEntries = (): LegendEntry[] => [
  {
    id: 'success',
    state: 'success',
    label: t('reports.monthly.ncSymbolLegend.labelComplete'),
    tooltipText: t('reports.monthly.ncSymbolLegend.complete'),
  },
  {
    id: 'success_date_changed',
    state: 'success',
    dateModified: true,
    label: t('reports.monthly.ncSymbolLegend.labelCompleteDateChanged'),
    tooltipText: t('reports.monthly.ncSymbolLegend.completeDateChanged'),
  },
  {
    id: 'failure',
    state: 'failure',
    label: t('reports.monthly.ncSymbolLegend.labelFailure'),
    tooltipText: t('reports.monthly.ncSymbolLegend.failure'),
  },
  {
    id: 'no_testing',
    state: 'no_testing',
    label: t('reports.monthly.ncSymbolLegend.labelNoTesting'),
    tooltipText: t('reports.monthly.ncSymbolLegend.noTesting'),
  },
  {
    id: 'override',
    state: 'override',
    label: t('reports.monthly.ncSymbolLegend.labelOverride'),
    tooltipText: t('reports.monthly.ncSymbolLegend.override'),
  },
  {
    id: 'confirmed',
    state: 'confirmed',
    label: t('reports.monthly.ncSymbolLegend.labelConfirmed'),
    tooltipText: t('reports.monthly.ncSymbolLegend.confirmed'),
  },
]

/**
 * Always-visible legend for NC calendar symbols.
 * Placed in the Monthly Report near the maintenance log calendar so auditors
 * can interpret symbol meaning without guessing (AC #1, AC #3, AC #4).
 * Uses getCellSymbol as the single source of truth so icons stay in sync with CalendarGrid.
 */
const NcSymbolLegend = () => {
  const legendEntries = buildLegendEntries()

  return (
    <div
      className="rounded-md border bg-muted/30 px-4 py-3"
      aria-label={t('reports.monthly.ncSymbolLegend.title')}
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('reports.monthly.ncSymbolLegend.title')}
      </p>
      <dl className="flex flex-wrap gap-x-6 gap-y-1.5">
        {legendEntries.map((entry) => (
          <Tooltip key={entry.id}>
            <TooltipTrigger asChild>
              <div className="flex cursor-default items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <SymbolIcon state={entry.state} dateModified={entry.dateModified} />
                </span>
                <dt className="sr-only">{entry.label}</dt>
                <dd className="whitespace-nowrap text-xs text-foreground">{entry.label}</dd>
              </div>
            </TooltipTrigger>
            <TooltipContent>{entry.tooltipText}</TooltipContent>
          </Tooltip>
        ))}
      </dl>
    </div>
  )
}

export default NcSymbolLegend
